import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { fetchLikes, savePassword } from "../store/actions/productActions";
import "../scss/components/_login.scss";
import { useTheme } from "../context/ThemeContext";
import { getTheme } from "../config/themes";

import { API } from "../config/api";
import { BRAND_NAME } from "../config/domain";
import { logAction, A } from "./AdminLogger";
import { logError } from "../ErrorLogger";

// Safe localStorage — кидає SecurityError в Safari Private Mode
function lsGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function lsSet(key, val) { try { localStorage.setItem(key, val); } catch {} }

const API_URL = API.otp;

// ── OTP Input — 6 окремих полів ──────────────────────────────
const OtpInput = ({ value, onChange, onEnter }) => {
  const inputs = useRef([]);
  const digits = (value + "      ").slice(0, 6).split("");

  const handleChange = (i, val) => {
    // Вставка тексту — витягуємо тільки цифри
    const clean = val.replace(/\D/g, "").slice(0, 6 - i);
    if (!clean) return;
    const arr = (value + "      ").slice(0, 6).split("");
    for (let k = 0; k < clean.length && i + k < 6; k++) arr[i + k] = clean[k];
    const next = arr.join("").trimEnd();
    onChange(next);
    const focus = Math.min(i + clean.length, 5);
    setTimeout(() => inputs.current[focus]?.focus(), 0);
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = (value + "      ").slice(0, 6).split("");
      if (arr[i] && arr[i].trim()) {
        arr[i] = " ";
      } else if (i > 0) {
        arr[i - 1] = " ";
        setTimeout(() => inputs.current[i - 1]?.focus(), 0);
      }
      onChange(arr.join("").trimEnd());
    } else if (e.key === "Enter" && value.replace(/\s/g,"").length === 6) {
      onEnter && onEnter();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const clean = pasted.replace(/\D/g, "").slice(0, 6);
    if (clean) {
      onChange(clean);
      setTimeout(() => inputs.current[Math.min(clean.length, 5)]?.focus(), 0);
    }
  };

  return (
    <div style={{display:"flex", gap:8, justifyContent:"center", margin:"8px 0"}}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          id={`otp-${i}`} name={`otp-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d.trim()}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          style={{
            width:42, height:54, textAlign:"center",
            fontSize:24, fontWeight:800, fontFamily:"monospace",
            border: d.trim() ? "2px solid var(--bv-primary,#d4500a)" : "2px solid var(--bv-border,#e8d5b0)",
            borderRadius:10,
            background: d.trim() ? "var(--bv-overlay-light,rgba(212,80,10,0.06))" : "var(--bv-bg-input,#fffbf3)",
            color:"var(--bv-text,#2a1200)",
            outline:"none",
            transition:"border-color 150ms, background 150ms",
            caretColor:"transparent",
          }}
        />
      ))}
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const activeLogo = getTheme(theme).logo;
  const [step, setStep]         = useState("email");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [phone, setPhone]       = useState("");
  const [address, setAddress]   = useState("");
  const [openTime, setOpenTime] = useState("08:00");
  const [message, setMessage]   = useState("");
  const [loading, setLoading]   = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetCode,    setResetCode]    = useState("");
  const [verifyLoading,    setVerifyLoading]    = useState(false);
  const [regCode,          setRegCode]          = useState("");
  const [regVerifyLoading, setRegVerifyLoading] = useState(false);
  const [tagIndex, setTagIndex] = useState(0);
  const TAGLINE_WORDS = ["PRODUCE", "WHOLESALE", "FRESH"];

  useEffect(() => {
    const t = setInterval(() => setTagIndex(i => (i + 1) % 3), 2200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (lsGet("userEmail")) navigate("/");
  }, [navigate]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const checkEmail = async () => {
    if (!email) return setMessage("Enter your email");
    if (!isValidEmail(email)) return setMessage("Please enter a valid email address");
    setLoading(true); setMessage("");
    try {
      const res = await axios.post(API_URL, { action: "check_email", email });
      setStep(res.data.found ? "login" : "register");
    } catch(e) { setMessage("Server error"); logError(e, "Login"); }
    setLoading(false);
  };

  const login = async () => {
    if (!password) return setMessage("Enter password from email");
    setLoading(true); setMessage("");
    try {
      const res = await axios.post(API_URL, { action: "login", email, password });
      if (res.data.success) {
        logAction(A.LOGIN, 'user', email,
          `Login success: ${email}`,
          null, { status: 'success', page: 'login' }
        );
          lsSet("userEmail", email);
        lsSet("bv_is_admin", res.data.is_admin ? "1" : "0");
        savePassword(password);
        dispatch(fetchLikes());
        navigate("/");
      } else {
        logAction(A.LOGIN, 'user', email,
          `Login failed: ${email} — ${res.data.error || 'wrong password'}`,
          null, { status: 'failed', page: 'login' }
        );
        setMessage(res.data.error || "Wrong password");
      }
    } catch(e) { setMessage("Server error"); logError(e, "Login"); }
    setLoading(false);
  };

  const register = async () => {
    if (!isValidEmail(email)) return setMessage("Please enter a valid email address");
    if (!name || !phone || !address) return setMessage("Fill all fields");
    setLoading(true); setMessage("");
    try {
      const res = await axios.post(API_URL, { action: "register_request", email, name, phone, address, open_time: openTime });
      if (res.data.success) {
        setStep("verifyReg");
        setRegCode("");
        setMessage("Enter the 6-digit code sent to your email.");
      } else setMessage(res.data.error || "Registration failed");
    } catch(e) { setMessage("Server error"); logError(e, "Login"); }
    setLoading(false);
  };

  const verifyReg = async () => {
    if (regCode.length !== 6) return setMessage("Enter the 6-digit code");
    setRegVerifyLoading(true); setMessage("");
    try {
      const res = await axios.post(API_URL, { action: "register_verify", email, code: regCode });
      if (res.data.success) {
        logAction(A.CREATE, 'user', email,
          `New account registered: ${email} · ${name||''}`.trim(),
          null, { status: 'success', page: 'login' }
        );
        setStep("enterPassword");
        setPassword("");
        setMessage("Account created! Password sent to your email.");
      } else setMessage(res.data.error || "Wrong code");
    } catch(e) { setMessage("Server error"); logError(e, "Login"); }
    setRegVerifyLoading(false);
  };

  const forgotPassword = async () => {
    if (!isValidEmail(email)) return setMessage("Enter a valid email first");
    setForgotLoading(true); setMessage("");
    try {
      const res = await axios.post(API_URL, { action: "forgot_password", email });
      if (res.data.success) {
        setStep("resetCode");
        setResetCode("");
        setMessage("Check your email for a 6-digit code.");
      } else setMessage(res.data.error || "Error");
    } catch(e) { setMessage("Server error"); logError(e, "Login"); }
    setForgotLoading(false);
  };

  const verifyReset = async () => {
    if (resetCode.length !== 6) return setMessage("Enter the 6-digit code");
    setVerifyLoading(true); setMessage("");
    try {
      const res = await axios.post(API_URL, { action: "verify_reset", email, code: resetCode });
      if (res.data.success) {
        setStep("enterPassword");
        setPassword("");
        setMessage("New password sent to your email.");
      } else setMessage(res.data.error || "Wrong code");
    } catch(e) { setMessage("Server error"); logError(e, "Login"); }
    setVerifyLoading(false);
  };

  const handleKey = (e, fn) => e.key === "Enter" && fn();

  const stepTitle = { email: "Welcome back", login: "Enter password", register: "Create account", enterPassword: "Check your email", forgot: "Forgot password?", resetCode: "Enter code", verifyReg: "Confirm email" };
  const stepSub   = { email: "Sign in to your wholesale store", login: email, register: "Tell us about your business", enterPassword: `We sent a password to ${email}`, forgot: "We'll send a confirmation code to your email", resetCode: `Code sent to ${email}`, verifyReg: `Code sent to ${email}` };

  const FieldIcon = ({ children }) => <span className="lgn__field-icon">{children}</span>;

  return (
    <div className="lgn">
      <div className="lgn__orb lgn__orb--1" />
      <div className="lgn__orb lgn__orb--2" />
      <div className="lgn__orb lgn__orb--3" />

      <div className="lgn__card">

        {/* LOGO */}
        <div className="lgn__logo-wrap">
          <div className="lgn__logo-ring lgn__logo-ring--1" />
          <div className="lgn__logo-ring lgn__logo-ring--2" />
          <div className="lgn__logo-ring lgn__logo-ring--3" />
          <div className={`lgn__logo-img-wrap${logoLoaded ? " lgn__logo-img-wrap--in" : ""}`}>
            <img
              src={activeLogo}
              alt={window._bvS?.brandName || BRAND_NAME}
              className="lgn__logo-img"
              onLoad={() => setLogoLoaded(true)}
            />
          </div>
        </div>

        {/* TAGLINE */}
        <div className="lgn__tagline">
          {TAGLINE_WORDS.map((word, i) => (
            <span
              key={word}
              className={`lgn__tagline-word ${
                i === tagIndex ? "active" :
                i === (tagIndex - 1 + 3) % 3 ? "prev" : "next"
              }`}
            >{word}</span>
          ))}
        </div>

        {/* HEADER */}
        <div className="lgn__header">
          <h1 className="lgn__title">{stepTitle[step]}</h1>
          <p className="lgn__sub">{stepSub[step]}</p>
        </div>

        {/* FORM */}
        <div className="lgn__form">

          {step === "email" && <>
            <div className="lgn__field">
              <FieldIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </FieldIcon>
              <input className="lgn__input" id="lgn-email" name="email" type="email" placeholder="Email address" value={email} autoComplete="email"
                onChange={e => setEmail(e.target.value)} onKeyDown={e => handleKey(e, checkEmail)} autoFocus />
            </div>
            <button className="lgn__btn" onClick={checkEmail} disabled={loading}>
              {loading ? <span className="lgn__spinner" /> : "Continue →"}
            </button>
          </>}

          {step === "login" && <>
            <div className="lgn__field">
              <FieldIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </FieldIcon>
              <input className="lgn__input" id="lgn-password" name="password" type={showPassword?"text":"password"} placeholder="Password from email" value={password} autoComplete="current-password"
                onChange={e => setPassword(e.target.value)} onKeyDown={e => handleKey(e, login)} autoFocus />
              <button type="button" className="lgn__eye" onClick={()=>setShowPassword(p=>!p)} tabIndex={-1}>{showPassword?(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>):(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
            </div>
            <button className="lgn__btn" onClick={login} disabled={loading}>
              {loading ? <span className="lgn__spinner" /> : "Sign In →"}
            </button>
            <button className="lgn__back" onClick={() => { setStep("email"); setMessage(""); setShowPassword(false); }}>← Back</button>
            <button className="lgn__forgot" onClick={() => { setStep("forgot"); setMessage(""); }}>Forgot password?</button>
          </>}

          {step === "register" && <>
            {[
              { ph: "Restaurant / Company Name", val: name,    set: setName,    type: "text", id: "lgn-name",    autoComplete: "organization", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
              { ph: "Phone number",              val: phone,   set: setPhone,   type: "tel",  id: "lgn-phone",   autoComplete: "tel", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg> },
              { ph: "Business address",          val: address, set: setAddress, type: "text", id: "lgn-address", autoComplete: "street-address", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> },
            ].map((f, i) => (
              <div className="lgn__field" key={i}>
                <FieldIcon>{f.icon}</FieldIcon>
                <input className="lgn__input" id={f.id} name={f.id} type={f.type} placeholder={f.ph} value={f.val} autoComplete={f.autoComplete} onChange={e => f.set(e.target.value)} />
              </div>
            ))}
            <div className="lgn__field lgn__field--time">
              <FieldIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </FieldIcon>
              <label className="lgn__time-label">Opening time</label>
              <input className="lgn__input lgn__input--time" id="lgn-open-time" name="open-time" type="time" value={openTime} autoComplete="off" onChange={e => setOpenTime(e.target.value)} />
            </div>
            <button className="lgn__btn" onClick={register} disabled={loading}>
              {loading ? <span className="lgn__spinner" /> : "Create Account →"}
            </button>
            <button className="lgn__back" onClick={() => { setStep("email"); setMessage(""); }}>← Back</button>
          </>}

          {step === "enterPassword" && <>
            <div className="lgn__email-sent">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <span>Check your inbox</span>
            </div>
            <div className="lgn__field">
              <FieldIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </FieldIcon>
              <input className="lgn__input" id="lgn-password-otp" name="password" type={showPassword?"text":"password"} placeholder="Enter password from email" value={password} autoComplete="current-password"
                onChange={e => setPassword(e.target.value)} onKeyDown={e => handleKey(e, login)} autoFocus />
              <button type="button" className="lgn__eye" onClick={()=>setShowPassword(p=>!p)} tabIndex={-1}>{showPassword?(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>):(<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
            </div>
            <button className="lgn__btn" onClick={login} disabled={loading}>
              {loading ? <span className="lgn__spinner" /> : "Open Store →"}
            </button>
          </>}

          {step === "verifyReg" && <>
            <OtpInput
              value={regCode}
              onChange={v => setRegCode(v.replace(/\D/g,"").slice(0,6))}
              onEnter={verifyReg}
            />
            <button className="lgn__btn" onClick={verifyReg} disabled={regVerifyLoading}>
              {regVerifyLoading ? <span className="lgn__spinner" /> : "Confirm Email →"}
            </button>
            <button className="lgn__back" onClick={() => { setStep("register"); setMessage(""); }}>← Back</button>
          </>}

          {step === "resetCode" && <>
            <OtpInput
              value={resetCode}
              onChange={v => setResetCode(v.replace(/\D/g,"").slice(0,6))}
              onEnter={verifyReset}
            />
            <button className="lgn__btn" onClick={verifyReset} disabled={verifyLoading}>
              {verifyLoading ? <span className="lgn__spinner" /> : "Verify Code →"}
            </button>
            <button className="lgn__back" onClick={() => { setStep("forgot"); setMessage(""); }}>← Back</button>
          </>}

          {step === "forgot" && <>
            <div className="lgn__field">
              <FieldIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </FieldIcon>
              <input className="lgn__input" id="lgn-forgot-email" name="email" type="email" placeholder="Your email address" value={email} autoComplete="email"
                onChange={e => setEmail(e.target.value)} onKeyDown={e => handleKey(e, forgotPassword)} autoFocus />
            </div>
            <button className="lgn__btn" onClick={forgotPassword} disabled={forgotLoading}>
              {forgotLoading ? <span className="lgn__spinner" /> : "Send New Password →"}
            </button>
            <button className="lgn__back" onClick={() => { setStep("login"); setMessage(""); }}>← Back</button>
          </>}

          {message && (
            <p className={`lgn__msg ${message.includes("sent") || message.includes("Password") ? "lgn__msg--ok" : "lgn__msg--err"}`}>
              {message}
            </p>
          )}
        </div>

        <p className="lgn__footer">{window._bvS?.brandName || BRAND_NAME} · Fresh Produce</p>
      </div>
    </div>
  );
};

export default Login;
